<script setup lang="ts">
import { Check, X, Radio, UserRound, Crown, CheckCircle } from 'lucide-vue-next'
import { router } from '@inertiajs/vue3'
import { computed } from 'vue'
import { BINDLE_LIVE_ON_SALE, BINDLE_LIVE_LAUNCH_PRICE } from '@/composables/useTutorPlans'

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface Props {
  tutorSlug: string
  tutorId?: number
  basePrice: number
  silverPrice: number
  goldPrice: number
  servicesOffered: string[]
  videosCount: number
  subjects: Array<{ id: number; name: string }>
  groupSubjects?: Array<{ id: number; name: string; levels: Array<{ id: number; name: string }> }>
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  groupSubjects: () => [],
  subscriptions: () => [],
})

/**
 * Two plans, in this order: Bindle Live, then 1:1.
 *
 * The three tiers underneath are `base` (content library), `silver` (group
 * tuition) and `gold` (one-to-one). Two of them no longer appear on their own:
 *
 *  - **Bindle Live takes the silver slot.** It is what group tuition becomes —
 *    a scheduled weekly class that runs whether or not a second student turns
 *    up. It is not built, so the card is marked and cannot be bought, and the
 *    price shown is the intended launch price rather than the tutor's old group
 *    rate, which was for a different product.
 *  - **The content library is not a plan.** Selling access to a tutor's material
 *    on its own put the cheapest, weakest option first in every comparison. It
 *    is included in both plans instead, and said so on each.
 *
 * Nothing is deleted underneath: the tiers, the prices and existing
 * subscriptions are untouched.
 *
 * The facts themselves live in `useTutorPlans`, because this card, the sidebar
 * card and the Overview summary all describe the same two plans and had each
 * worked them out separately.
 */
const activeSub = computed(() => props.subscriptions.find((s) => s.teacher_id === props.tutorId))
const isSubscribed = computed(() => !!activeSub.value)
const subscribedTier = computed(() => activeSub.value?.tier ?? null)

const offersOneOnOne = computed(() => props.servicesOffered.includes('one_on_one'))

const contentLine = computed(() =>
  props.videosCount > 0
    ? `Access to this tutor's ${props.videosCount} videos and study material`
    : "Access to this tutor's study material as it is published"
)

const plans = computed(() => [
  {
    id: 'live',
    tier: 'silver',
    name: 'Bindle Live',
    icon: Radio,
    price: BINDLE_LIVE_LAUNCH_PRICE,
    priceNote: 'Planned launch price',
    description:
      'A live masterclass every week following a structured course, recorded into your library so a missed week is not a lost week.',
    features: [
      contentLine.value,
      'Live weekly group classes',
      'Every class recorded and kept',
      'Q&A with the tutor after each class',
    ],
    available: BINDLE_LIVE_ON_SALE,
    accent: 'live',
  },
  {
    id: 'oneOnOne',
    tier: 'gold',
    name: '1:1 Tutoring',
    icon: UserRound,
    price: props.goldPrice,
    description:
      'A weekly one-to-one lesson with this tutor, on what your child is actually stuck on, at a time you agree together.',
    features: [
      contentLine.value,
      'One one-to-one lesson a week',
      'A study plan built around your child',
      'Direct messaging between lessons',
    ],
    available: offersOneOnOne.value && props.goldPrice > 0,
    accent: 'brand',
  },
])

function isSubscribedToPlan(tier: string): boolean {
  return subscribedTier.value === tier
}

function handleSubscribe(tier: string) {
  if (isSubscribed.value) return
  router.visit(`/payment?tutor=${props.tutorSlug}&tier=${tier}`)
}

/**
 * One row per feature, one column per plan, both from the same source — a row
 * cannot end up with a different number of cells than its heading, which is how
 * the previous hand-written table broke when a column was withdrawn.
 */
/**
 * Recordings are not a row here.
 *
 * They belong to Bindle Live, which describes them on its own card, and the row
 * only existed to put a cross against 1:1 — advertising a gap rather than a
 * feature. The intention is that one-to-one lessons are recorded too, but they
 * are not: the FAQ says so plainly, and ticking the row before that is built
 * would be a promise the product cannot keep. It comes back as a tick in both
 * columns once session recording exists.
 */
const comparisonFeatures = computed(() => [
  { label: contentLine.value, plans: ['live', 'oneOnOne'] },
  { label: 'Live weekly group classes', plans: ['live'] },
  { label: 'Weekly one-to-one lesson', plans: ['oneOnOne'] },
  { label: 'Study plan built around your child', plans: ['oneOnOne'] },
  { label: 'Direct messaging with your tutor', plans: ['oneOnOne'] },
])
</script>

<template>
  <div>
    <h3 class="mb-4 text-xl font-semibold text-ink">Plans</h3>

    <div class="mb-6 grid gap-4 sm:grid-cols-2">
      <article
        v-for="plan in plans"
        :key="plan.id"
        class="relative flex flex-col rounded-xl border p-5"
        :class="plan.available ? 'border-line bg-surface' : 'border-dashed border-line-strong bg-surface-2'"
      >
        <div class="flex items-start justify-between gap-3">
          <span
            class="grid size-12 place-items-center rounded-full"
            :class="plan.accent === 'live' ? 'bg-live-soft text-live-soft-text' : 'bg-brand-soft text-brand'"
          >
            <component :is="plan.icon" class="size-6" />
          </span>

          <span
            v-if="isSubscribedToPlan(plan.tier)"
            class="inline-flex items-center gap-1 rounded-full bg-success-bg px-2.5 py-1 text-[11px] font-bold text-success"
          >
            <CheckCircle class="size-3" /> Subscribed
          </span>
          <span
            v-else-if="!plan.available"
            class="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide"
            :class="plan.accent === 'live' ? 'bg-live-soft text-live-soft-text' : 'bg-surface-2 text-muted'"
          >
            {{ plan.id === 'live' ? 'Launching soon' : 'Not offered' }}
          </span>
          <span
            v-else-if="plan.id === 'oneOnOne'"
            class="inline-flex items-center gap-1 rounded-full bg-brand-soft px-2.5 py-1 text-[11px] font-bold text-brand-soft-text"
          >
            <Crown class="size-3" /> Most popular
          </span>
        </div>

        <h4
          class="mt-4 font-sans text-lg font-bold uppercase tracking-[0.08em]"
          :class="plan.accent === 'live' ? 'text-live-soft-text' : 'text-brand-soft-text'"
        >
          {{ plan.name }}
        </h4>
        <p class="mt-2 text-sm leading-relaxed text-muted">{{ plan.description }}</p>

        <p v-if="plan.price > 0" class="mt-4">
          <span v-if="plan.priceNote" class="block text-xs text-subtle">{{ plan.priceNote }}</span>
          <span
            class="font-display text-3xl font-bold"
            :class="plan.accent === 'live' ? 'text-live-soft-text' : 'text-ink'"
          >£{{ Number(plan.price).toFixed(2) }}</span>
          <span class="text-sm text-muted">/week</span>
        </p>

        <ul class="mt-4 space-y-2">
          <li v-for="feature in plan.features" :key="feature" class="flex gap-2 text-sm text-muted">
            <Check
              class="mt-0.5 size-4 shrink-0"
              :class="plan.accent === 'live' ? 'text-live' : 'text-brand'"
            />
            {{ feature }}
          </li>
        </ul>

        <!--
          `mt-auto` pins the action to the bottom of its card. The two cards are
          the same height — the grid stretches them — but their contents are not:
          Bindle Live carries a "planned launch price" line that 1:1 does not, so
          without this the two buttons sat at different heights beside each other.
        -->
        <div class="mt-auto pt-6">
          <button
            v-if="isSubscribedToPlan(plan.tier)"
            class="w-full cursor-default rounded-lg bg-success-bg py-2.5 font-semibold text-success"
            disabled
          >
            Your plan
          </button>
          <button
            v-else-if="!plan.available"
            class="w-full cursor-not-allowed rounded-lg py-2.5 font-semibold opacity-60"
            :class="plan.accent === 'live' ? 'bg-live text-white' : 'bg-surface-2 text-muted'"
            disabled
          >
            {{ plan.id === 'live' ? 'Coming soon!' : 'Not available' }}
          </button>
          <button
            v-else
            class="w-full rounded-lg bg-brand py-2.5 font-semibold text-brand-contrast transition-colors hover:bg-brand-hover"
            :disabled="isSubscribed"
            @click="handleSubscribe(plan.tier)"
          >
            {{ isSubscribed ? 'You have another plan' : 'Subscribe' }}
          </button>
        </div>
      </article>
    </div>

    <!--
      On a narrow screen this becomes one block per plan rather than a table
      scrolled sideways: a comparison you have to pan across is not a comparison.
    -->
    <div class="rounded-2xl bg-surface-2 p-4">
      <h4 class="mb-3 text-center text-base font-semibold text-ink">Plan features</h4>

      <table class="hidden w-full sm:table">
        <thead>
          <tr class="border-b border-line">
            <th class="px-3 py-2 text-left text-sm font-semibold text-muted">Feature</th>
            <th
              v-for="plan in plans"
              :key="plan.id"
              class="px-3 py-2 text-center text-sm font-semibold text-ink"
            >{{ plan.name }}</th>
          </tr>
        </thead>
        <tbody class="text-xs">
          <tr v-for="feature in comparisonFeatures" :key="feature.label" class="border-b border-line last:border-0">
            <td class="px-3 py-2.5 font-medium text-ink">{{ feature.label }}</td>
            <td v-for="plan in plans" :key="plan.id" class="px-3 py-2.5 text-center">
              <Check v-if="feature.plans.includes(plan.id)" class="inline size-4 text-brand" />
              <X v-else class="inline size-4 text-subtle" />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="space-y-3 sm:hidden">
        <div v-for="plan in plans" :key="plan.id" class="rounded-xl border border-line bg-surface p-4">
          <p class="text-sm font-semibold text-ink">{{ plan.name }}</p>
          <ul class="mt-2.5 space-y-1.5">
            <li
              v-for="feature in comparisonFeatures"
              :key="feature.label"
              class="flex items-start gap-2 text-xs"
              :class="feature.plans.includes(plan.id) ? 'text-ink' : 'text-subtle line-through'"
            >
              <Check v-if="feature.plans.includes(plan.id)" class="mt-0.5 size-3.5 shrink-0 text-brand" />
              <X v-else class="mt-0.5 size-3.5 shrink-0" />
              {{ feature.label }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
